/**
 * Zod-based handle schemas for users, organizations, and enterprises.
 *
 * Relocated verbatim from `@buildwithflux/models` so that low-level packages
 * (e.g. URL routing) can consume handle schemas without depending on the full
 * models package. `@buildwithflux/models` re-exports everything here for
 * backward compatibility.
 */
import z from "zod";

import {EnterpriseUid, OrganizationUid, UserUid} from "./legacy.js";

/**
 * A user handle is a unique string, chosen by the user, that acts as the main username
 *
 * The username appears in the path to the user document, and also as the "handle" property on the IUserData. It also
 * appears as the key on the separate usernames collection (used for reserving usernames, see the HandleRepository)
 *
 * aspirational: brand this as a zod string one day?
 */
export const UserHandle = z.string().min(3);
export type UserHandle = z.infer<typeof UserHandle>;

/**
 * An organization handle is a unique string, chosen by the user, that acts as the main username of the organization
 *
 * The username appears in the path to the organization document, and also as the "handle" property on the Organization. It also
 * appears as the key on the separate usernames collection (used for reserving usernames, see the HandleRepository)
 */
export const OrganizationPublicHandle = z.string().brand("OrganizationPublicHandle");
export type OrganizationPublicHandle = z.infer<typeof OrganizationPublicHandle>;

export type HandleMapping = HandleMappingForUser | HandleMappingForOrganization;

/**
 * Maps between a user UID and a user handle.
 */
export interface HandleMappingForUser {
    user_uid: UserUid;
    organizationUid: undefined;
    userHandle: UserHandle | OrganizationPublicHandle;
}

/**
 * Maps between a organization UID and a user handle.
 */
export interface HandleMappingForOrganization {
    user_uid: undefined;
    organizationUid: OrganizationUid;
    userHandle: UserHandle | OrganizationPublicHandle;
}

// eslint-disable-next-line no-restricted-syntax
export function isHandleMappingForUser(data: unknown): data is HandleMappingForUser {
    return (
        typeof data === "object" &&
        !!data &&
        !!(data as HandleMappingForUser)?.userHandle &&
        !!(data as HandleMappingForUser)?.user_uid &&
        !(data as HandleMappingForUser)?.organizationUid
    );
}

// eslint-disable-next-line no-restricted-syntax
export function isHandleMappingForOrganization(data: unknown): data is HandleMappingForOrganization {
    return (
        typeof data === "object" &&
        !!data &&
        !!(data as HandleMappingForUser)?.userHandle &&
        !!(data as HandleMappingForUser)?.organizationUid &&
        !(data as HandleMappingForUser)?.user_uid
    );
}

/**
 * An enterprise handle follows the same rules as a user/organization handle, but forms a separate unique namespace
 */
export const EnterpriseHandle = z
    .string()
    .min(3)
    .regex(/[a-z][-a-z0-9]{2,}/)
    .brand("EnterpriseHandle");
export type EnterpriseHandle = z.infer<typeof EnterpriseHandle>;

/**
 * Enterprise handles need to be unique across all enterprises, and this mapping entry ensures that is the case.
 *
 * It is stored keyed by the enterprise handle, and contains the enterprise UID that owns the handle. Because
 * we don't yet support self-signup for enterprises (because it may give away existing enterprise handles via
 * form validation), these entries are usually created by an admin backend process
 */
export const EnterpriseHandleMapping = z.object({
    handle: EnterpriseHandle,
    enterpriseUid: EnterpriseUid,
});
export type EnterpriseHandleMapping = z.infer<typeof EnterpriseHandleMapping>;

/**
 * An enterprise handle follows the same rules as a user/organization handle, but forms a separate unique namespace
 */
export const OrganizationPrivateHandle = z
    .string()
    .min(3)
    .regex(/[a-z][-a-z0-9]{2,}/)
    .brand("OrganizationPrivateHandle");
export type OrganizationPrivateHandle = z.infer<typeof OrganizationPrivateHandle>;

/**
 * Private organizations belonging to an enterprise need to have unique handles within that enterprise, and this mapping
 * entry ensures that is the case.
 *
 * It is stored keyed by the organization handle, but in a subcollection of the enterprise handle document. These may
 * be created by an owner of the enterprise when they create an organization (and that is done by a backend API call)
 */
export interface HandleMappingForPrivateOrganization {
    handle: OrganizationPrivateHandle;
    organizationUid: OrganizationUid;
}

export const OrganizationHandle = OrganizationPrivateHandle;
export type OrganizationHandle = OrganizationPublicHandle | OrganizationPrivateHandle;
